import { useEffect, useState } from 'react';
import { Dropdown } from '../helpers/Dropdown';
import { FileDrop } from '../helpers/FileDrop';
import '../fileImports/NewFileUploadPage.css';
import { useNavigate } from 'react-router-dom';
import {
    postImportData,
    getImportTypeData,
    getImportFileTypeData,
    getAllImportTemplatesForTenant,
    getImportTemplate,
} from '../../services/importService';
import Spinner from '../ui/spinners/Spinner';
import Toast from '../ui/toasts/Toast';
import { isValidImportFileTypeAndSize } from '../../utils/validate';
import { LogEventType } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import { ROUTES } from '../../constants/routes';
import { useAuthContext } from '../../store/AuthContext';
import { TenantTiers, ImportType } from '../../utils/data/enums';
import { getText } from '../../i18n';

const getOrderedTemplateTypes = (templateTypes = []) =>
    [...templateTypes].sort(
        (a, b) => (a.processingOrder ?? 0) - (b.processingOrder ?? 0),
    );

const getFirstTemplateTypeImportTypeId = template => {
    const orderedTemplateTypes = getOrderedTemplateTypes(
        template?.templateTypes || [],
    );

    return orderedTemplateTypes[0]?.importTypeId || null;
};

const isMultiTemplate = template =>
    template?.importTypeId === 0 ||
    (template?.type || '').toLowerCase() ===
    getText('msg_09cb6197bf14').toLowerCase() ||
    (template?.templateTypes || []).length > 1;

const getTemplateDisplayImportType = template => {
    const explicitType = (template?.type || template?.importType || '').trim();

    if (explicitType.length > 0) {
        return explicitType;
    }

    if (
        template?.importTypeId === 0 ||
        (template?.templateTypes || []).length > 1
    ) {
        return getText('msg_09cb6197bf14');
    }

    return '';
};

const buildLayoutsForCategory = ({
    categoryValue,
    categories,
    allTemplates
}) => {

    if (!categoryValue) {
        return [];
    }

    const selectedCategory = categories.find(
        element => element.value === categoryValue,
    );

    const customTemplates = (allTemplates || []).filter(
        template => template.importTypeIds.some(importTypeId => importTypeId === categoryValue),
    );

    return [
        {
            value: 0,
            label: `Standard ${selectedCategory?.label || ''}`,
        },
        ...customTemplates.map(template => ({
            value: template.id,
            label:
                getTemplateDisplayImportType(template) ===
                    getText('msg_09cb6197bf14')
                    ? `${template.name} — ${getText('msg_09cb6197bf14')}`
                    : template.name,
        })),
    ].sort((a, b) => a.label.localeCompare(b.label));
};

const NewFileUploadPage = () => {
    const { isSelfRegistrationEnabled, tenantTierId } = useAuthContext();
    const [category, setCategory] = useState(0);
    const [allTemplates, setAllTemplates] = useState([]);
    const [templateDetailsById, setTemplateDetailsById] = useState({});
    const [importTemplateId, setImportTemplateId] = useState(null);
    const [layoutDisabled, setLayoutDisabled] = useState(true);
    const [processFormat, setProcessFormat] = useState(0);
    const [categories, setCateories] = useState([]);
    const [layouts, setLayouts] = useState([]);
    const [processFormats, setProcessFormats] = useState([]);
    const [file, setFile] = useState();
    const errorThresholdPercentageMin = 1;
    const errorThresholdPercentageMax = 100;
    const [errorThresholdPercentages, setErrorThresholdPercentages] = useState(
        [],
    );
    const [errorThresholdPercentage, setErrorThresholdPercentage] = useState(
        errorThresholdPercentageMax,
    );
    const [test, setTest] = useState(false);
    const navigate = useNavigate();
    const [isSaving, setIsSaving] = useState(false);
    const selectedTemplate =
        (allTemplates || []).find(
            template => template.id === importTemplateId,
        ) || null;

    const handleFileChange = fileValue => {
        setFile(fileValue);
    };

    const validateForm = () => {
        if (!category) return getText('msg_d8bd3bb24b18');
        if ((importTemplateId ?? -1) < 0) return getText('msg_e5d18012420b');
        if (!processFormat) return getText('msg_e164642f46ee');
        if (!file) return getText('msg_3bad71815a76');

        const fileTypeAndSizeValidation = isValidImportFileTypeAndSize(file);
        if (fileTypeAndSizeValidation) return fileTypeAndSizeValidation;

        return null;
    };

    const handleCategoryChange = categoryValue => {
        setCategory(categoryValue);
        setImportTemplateId(null);
        setLayoutDisabled(!categoryValue);
    };

    const handleLayoutChange = layoutValue => {
        setImportTemplateId(layoutValue);
    };

    const handleProcessFormatChange = processFormatValue => {
        setProcessFormat(processFormatValue);
    };

    const handleErrorThresholdPercentageChange =
        errorThresholdPercentageValue => {
            setErrorThresholdPercentage(errorThresholdPercentageValue);
        };

    const getImportTypeIdForSubmission = async () => {
        if (!selectedTemplate || importTemplateId === 0) {
            return category;
        }

        if (!isMultiTemplate(selectedTemplate)) {
            return category;
        }

        const detailedTemplate =
            (await getDetailedTemplateForSubmission(selectedTemplate)) ||
            templateDetailsById[selectedTemplate.id];

        const firstTemplateTypeImportTypeId =
            getFirstTemplateTypeImportTypeId(detailedTemplate);

        return firstTemplateTypeImportTypeId || category;
    };

    const submitUpload = async () => {
        const errorMessage = validateForm();

        if (errorMessage) {
            Toast.error(errorMessage);
            return;
        }

        setIsSaving(true);

        try {
            const submissionImportTypeId = await getImportTypeIdForSubmission();

            await postImportData(
                submissionImportTypeId,
                errorThresholdPercentage,
                test,
                processFormat,
                file,
                importTemplateId,
            );

            setTimeout(function () {
                navigate(ROUTES.IMPORT_QUEUE);
            }, 0);
        } catch (error) {
            console.error('Error saving import file', error);
            if (error?.response?.data) {
                Toast.error(error.response.data);
            } else {
                Toast.error(getText('msg_76ab5b2b6385'));
            }
        } finally {
            setIsSaving(false);
        }
    };

    const getDetailedTemplateForSubmission = async template => {
        if (!template) {
            return null;
        }

        if (!isMultiTemplate(template)) {
            return null;
        }

        const existingDetail = templateDetailsById[template.id];
        if (existingDetail) {
            return existingDetail;
        }

        try {
            const detailedTemplate = await getImportTemplate(template.id);

            setTemplateDetailsById(prev => ({
                ...prev,
                [template.id]: detailedTemplate,
            }));

            return detailedTemplate;
        } catch (error) {
            console.error(
                `Error retrieving template detail for template ${template.id}`,
                error,
            );
            return null;
        }
    };

    useEffect(() => {
        getImportTypeData()
            .then(function (response) {
                var allImportTypes = response.map(item => ({
                    value: item.id,
                    label: item.name,
                }));

                const shouldFilterImportTypes =
                    isSelfRegistrationEnabled ||
                    tenantTierId === TenantTiers.Base;

                if (shouldFilterImportTypes) {
                    const filteredImportTypes = allImportTypes.filter(
                        item =>
                            item.value !== ImportType.Demographic &&
                            item.value !== ImportType.Enrollment,
                    );
                    setCateories(filteredImportTypes);
                    return;
                }

                setCateories(allImportTypes);
            })
            .catch(function (error) {
                console.error(error);
            });

        getImportFileTypeData()
            .then(function (response) {
                let newProcessFormats = [];
                for (let i = 0; i < response.length; i++) {
                    const item = response[i];
                    const newItem = {
                        value: item.id,
                        label: item.name,
                    };
                    newProcessFormats.push(newItem);
                }
                setProcessFormats(newProcessFormats);
            })
            .catch(function (error) {
                console.error(error);
            });

        let newErrorThresholdPercentages = [];
        for (
            let i = errorThresholdPercentageMax;
            i >= errorThresholdPercentageMin;
            i--
        ) {
            const newErrorThresholdPercentage = {
                value: i,
                label: i,
            };
            newErrorThresholdPercentages.push(newErrorThresholdPercentage);
        }
        setErrorThresholdPercentages(newErrorThresholdPercentages);

        getAllImportTemplatesForTenant()
            .then(function (response) {
                setAllTemplates(response || []);
            })
            .catch(function (error) {
                console.error(error);
            });
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadLayouts = async () => {
            if (!category) {
                setLayouts([]);
                return;
            }

            const multiTemplatesNeedingDetails = (allTemplates || []).filter(
                template =>
                    isMultiTemplate(template) &&
                    !templateDetailsById[template.id],
            );

            let mergedTemplateDetails = templateDetailsById;

            if (multiTemplatesNeedingDetails.length > 0) {
                const detailEntries = await Promise.all(
                    multiTemplatesNeedingDetails.map(async template => {
                        try {
                            const detailedTemplate = await getImportTemplate(
                                template.id,
                            );
                            return [template.id, detailedTemplate];
                        } catch (error) {
                            console.error(
                                `Error retrieving template detail for template ${template.id}`,
                                error,
                            );
                            return [template.id, null];
                        }
                    }),
                );

                if (cancelled) return;

                mergedTemplateDetails = {
                    ...templateDetailsById,
                    ...Object.fromEntries(detailEntries),
                };

                setTemplateDetailsById(mergedTemplateDetails);
            }

            const nextLayouts = buildLayoutsForCategory({
                categoryValue: category,
                categories,
                allTemplates,
                templateDetailsById: mergedTemplateDetails,
            });

            if (!cancelled) {
                setLayouts(nextLayouts);
            }
        };

        loadLayouts();

        return () => {
            cancelled = true;
        };
    }, [category, categories, allTemplates, templateDetailsById]);

    return (
        <div className="page-content">
            {isSaving && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        zIndex: 1000,
                        pointerEvents: 'auto',
                    }}
                >
                    <Spinner width={50} className="loadingSpinner" />
                </div>
            )}

            <div className="card">
                <div className="card-body px-0 py-4">
                    {/* Page Header with Title and Action */}
                    <div className="d-flex justify-content-between">
                        {/* Page Title */}
                        <p className="page-title float-start d-inline card-title ms-4">
                            {getText('msg_ac4212282494')}
                        </p>
                        {/* Page Actions */}
                        <div className="float-end">
                            <a
                                type="button"
                                className="ing-text-buttons d-inline mx-3"
                                href="/import template.xlsx"
                                onClick={() => {
                                    logEvent(
                                        LogEventType.DataDownloaded,
                                        `Imports specs downloaded`,
                                    );
                                }}
                            >
                                <i className="bi bi-cloud-download me-2" />
                                {getText('msg_7d5a5efa475c')}
                            </a>
                        </div>
                    </div>
                    <div className="page-body">
                        <div className="dropdown-form-container">
                            <div className="file-form-label">
                                {getText('msg_78cfb1f529c1')}
                                <span className="text-danger">*</span>
                            </div>
                            <Dropdown
                                defaultValue={getText('msg_7a7d6f0274ce')}
                                options={categories}
                                onChange={handleCategoryChange}
                            />
                        </div>

                        {/* 1) Start of Disabled Dropdown */}
                        <div className="dropdown-form-container">
                            <div className="file-form-label">
                                {getText('msg_972ad8d8a503')}
                                <span className="text-danger">*</span>
                            </div>
                            <Dropdown
                                defaultValue={getText('msg_7a7d6f0274ce')}
                                options={layouts}
                                onChange={handleLayoutChange}
                                disabled={layoutDisabled}
                            />
                        </div>

                        <div className="dropdown-form-container">
                            <div className="file-form-label">
                                {getText('msg_12c8f2c6b412')}
                                <span className="text-danger">*</span>
                            </div>
                            <Dropdown
                                defaultValue={getText('msg_7a7d6f0274ce')}
                                options={processFormats}
                                onChange={handleProcessFormatChange}
                            />
                        </div>
                        <div className="dropdown-form-container">
                            <div className="file-form-label">
                                {getText('msg_86d19e1c5d48')}
                            </div>
                            <Dropdown
                                defaultValue={errorThresholdPercentageMax}
                                options={errorThresholdPercentages}
                                onChange={handleErrorThresholdPercentageChange}
                            />
                        </div>
                        <div className="form-checkbox-container">
                            <input
                                type="checkbox"
                                checked={test}
                                onChange={e => setTest(e.target.checked)}
                                className="form-check-input custom-checkbox"
                            />
                            {getText('msg_640ab2bae07b')}
                        </div>
                        <FileDrop file={file} onChange={handleFileChange} />
                        <div className="upload-file">
                            <button
                                className="ing-button-primary-left"
                                type="submit"
                                onClick={submitUpload}
                                disabled={isSaving}
                            >
                                {getText('msg_dcf4cc69bca4')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewFileUploadPage;
